<template>
  <section class="grid-block">
    <header>Предыдущие заказы товара</header>

    <div class="content">
      <VDataGridV2
        v-if="demandV2"
        :data="prevDeals"
        layout="fill"
        @row:dblclick="row => openPositionCard(row)"
      >
        <VDataGridColumnV2
          id="seller"
          width="200px"
          content-class="ellipsis"
        >
          <template #label>
            Поставщик
            <div class="description">
              Производитель
            </div>
          </template>
          <template #default="{ row }">
            <div class="nowrap ellipsis">
              {{ row.seller?.name }}
            </div>
            <div class="description nowrap ellipsis">
              {{ row.manufacturer?.name || '--' }}
            </div>
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          id="price"
          width="150px"
          content-align="right"
          content-class="ellipsis"
        >
          <template #label>
            Цена
          </template>
          <template #default="{ row }">
            <div class="nowrap ellipsis">
              {{ row.priceFullSeller?.format(row.currencyCode) || '--' }}
            </div>
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          id="date"
          content-align="center"
        >
          <template #label>
            Дата доставки
          </template>
          <template #default="{ row }">
            {{ row.desiredReceiptDate?.format() || '--' }}
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          id="actions"
          width="50px"
          content-align="center"
        >
          <template #label/>
          <template #default="{ row }">
            <VButton @click="openPositionCard(row)">
              <template #icon>
                <VIcon material="open_in_new" theme="outlined" />
              </template>
            </VButton>
          </template>
        </VDataGridColumnV2>

        <VDataGridActionBarV2 v-if="sortedPrevDeals.length > 2">
          <template #default>
            <div class="btn-wrap">
              <VButton
                theme="link"
                @click="showAllDeals = !showAllDeals"
              >
                {{ showAllDealsButtonLabel }}
              </VButton>
            </div>
          </template>
        </VDataGridActionBarV2>
      </VDataGridV2>
    </div>
  </section>
</template>

<script lang="ts">

import DemandV2 from '@/domains/DemandsV2/DemandV2';

import type OrderPosition from '@/domains/Orders/OrderPosition';

export default defineComponent({
  name: 'DemandV2PrevDealsSection',

  props: {
    demandV2: {
      type:     DemandV2,
      required: true,
    },
  },

  data() {
    return {
      /** по умолчанию показываем две последние сделки */
      showAllDeals: false,
    };
  },

  computed: {
    /**
     * Сделки, отсортированные по дате доставки (свежие сверху).
     * prevDeals у demandV2 — позиции заказов (OrderPosition), не сами заказы.
     * sort мутирует массив, поэтому копируем поверхностно — вложенные поля
     * сортировка не трогает, глубокая копия не нужна.
     */
    sortedPrevDeals(): OrderPosition[] {
      if (this.$core.utils.isNotEmptyArray(this.demandV2?.prevDeals) === false) {
        return [];
      }
      else {
        /** compareTo у DateSimple: отрицательное — раньше. Свежие сверху,
         *  поэтому знак разворачиваем. Прежний компаратор равенство не
         *  различал и на равных датах возвращал -1. */
        return [...this.demandV2.prevDeals].sort((a, b) => {
          return b.desiredReceiptDate.compareTo(a.desiredReceiptDate);
        });
      }
    },

    /**
     * Что показываем в гриде: все сделки или две последние.
     * slice возвращает новый массив, исходный sortedPrevDeals не мутируется.
     */
    prevDeals(): OrderPosition[] {
      if (this.showAllDeals) {
        return this.sortedPrevDeals;
      }
      else {
        return this.sortedPrevDeals.slice(0, 2);
      }
    },

    /** Надпись на кнопке разворачивания списка. */
    showAllDealsButtonLabel(): string {
      if (this.showAllDeals) {
        return 'Скрыть сделки';
      }
      else {
        return 'Показать ещё';
      }
    },
  },

  methods: {
    /**
     * Открыть карточку позиции заказа по id.
     * row здесь — OrderPosition из demandV2.prevDeals: row.id — id позиции,
     * row.isPreorder — флаг предзаказа. Карточка грузит позицию сама по id.
     */
    async openPositionCard(row: OrderPosition): Promise<void> {
      await this.$core.dialogs.OrderPositionCard.showAsPromise({
        id:          row.id,
        isPreorder:  row.isPreorder,
        isAdminMode: this.$core.currentUser.isAdmin,
      });
    },
  },
});
</script>
